import type { IAudienceVideoInputDevice } from '@lucimark/shared/models/playback-heartbeat'

const CACHE_TTL_MS = 45_000
let cache: { at: number, list: IAudienceVideoInputDevice[] } | null = null

/**
 * After WebRTC preview releases the camera, block secondary consumers from opening a new stream briefly:
 * heartbeat enumeration probe and audience analytics motion sampling (both would fight the OS “camera on” indicator).
 */
let secondaryAudienceCameraAcquireSuppressedUntil = 0

export function suppressAudienceVideoInputDeviceProbeForMs(ms: number): void {
  secondaryAudienceCameraAcquireSuppressedUntil = Math.max(
    secondaryAudienceCameraAcquireSuppressedUntil,
    Date.now() + ms
  )
}

/** True while we should not call `getUserMedia` for non-preview features (heartbeat probe, motion pipeline). */
export function isAudienceSecondaryCameraAcquireSuppressed(): boolean {
  return Date.now() < secondaryAudienceCameraAcquireSuppressedUntil
}

/** Clears the in-memory enumeration cache so the next heartbeat re-queries devices (e.g. after operator “refresh list”). */
export function invalidateAudienceVideoInputDevicesCache(): void {
  cache = null
}

export interface IGetCachedAudienceVideoInputDevicesOptions {
  /** When true, skip the permission probe: preview already holds the camera; avoids a second stream. */
  isWebRtcPreviewActive?: () => boolean
}

/**
 * Lists `videoinput` devices for playback-heartbeat when the tenant enables audience webcam preview or analytics (web / Android WebView).
 * Caches briefly. May call `getUserMedia({ video: true })` once so browsers expose non-empty `label` values.
 */
export async function getCachedAudienceVideoInputDevicesForHeartbeat(
  options?: IGetCachedAudienceVideoInputDevicesOptions
): Promise<IAudienceVideoInputDevice[]> {
  if (typeof navigator === 'undefined' || navigator.mediaDevices?.enumerateDevices == null) {
    return []
  }
  const now = Date.now()
  if (cache != null && now - cache.at < CACHE_TTL_MS) {
    return cache.list
  }
  try {
    let list = await navigator.mediaDevices.enumerateDevices()
    let videoInputs = list.filter(d => d.kind === 'videoinput')
    // Android WebView often returns no videoinput entries (or only opaque ids with empty labels)
    // until camera permission is granted via getUserMedia — probing fixes an empty dropdown in the app.
    const previewActive = options?.isWebRtcPreviewActive?.() === true
    const probeTemporarilySuppressed = isAudienceSecondaryCameraAcquireSuppressed()
    const shouldProbeCamera = !previewActive
      && !probeTemporarilySuppressed
      && (videoInputs.length === 0 || videoInputs.some(d => !d.label))
    if (shouldProbeCamera) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true })
        for (const t of stream.getTracks()) {
          t.stop()
        }
      }
      catch {
        // Denied or no camera: still re-enumerate in case the platform exposes devices anyway
      }
      list = await navigator.mediaDevices.enumerateDevices()
      videoInputs = list.filter(d => d.kind === 'videoinput')
    }
    const out: IAudienceVideoInputDevice[] = list
      .filter(d => d.kind === 'videoinput')
      .map(d => ({
        deviceId: d.deviceId,
        label: (d.label && d.label.trim() !== '') ? d.label : d.deviceId
      }))
      .filter(d => d.deviceId.trim() !== '')
    cache = { at: now, list: out }
    return out
  }
  catch {
    return cache?.list ?? []
  }
}
